/**
 * Prompts Library List Module
 * 
 * Displays the prompt library with filters, status badges, and management actions.
 * Respects RBAC permissions for creating, approving, and deprecating prompts.
 */

import logger from '../../core/logger.js';
import { promptsService } from '../../services/prompts-service.js';
import { can, isAdmin } from '../../utils/rbac-helpers.js';
import { Permission } from '../../types/rbac.js';
import { PromptVersion, PromptStatus, PromptsFilters } from '../../types/prompt-model.js';
import toast from '../../ui/toast.js';

/**
 * Prompts list callbacks
 */
interface PromptsListCallbacks {
  onEditPrompt: (promptId: string) => void;
  onCreatePrompt: () => void;
}

let callbacks: PromptsListCallbacks | null = null;

/**
 * Register callbacks
 */
export function registerPromptsListCallbacks(cbs: PromptsListCallbacks): void {
  callbacks = cbs;
}

/**
 * Current filters and pagination
 */
let currentFilters: PromptsFilters = {};
let currentPage = 1;
const perPage = 20;

/**
 * Render the prompts library view
 */
export async function renderPromptsLibraryView(container: HTMLElement): Promise<void> {
  logger.info('admin: Rendering prompts library');
  
  // Clear container
  container.innerHTML = '';
  
  // Check permissions - VIEW_PROMPTS available to all authenticated users
  if (!can(Permission.VIEW_PROMPTS)) {
    renderAccessDenied(container);
    return;
  }
  
  // Create header
  const header = document.createElement('div');
  header.className = 'admin-header';
  
  const heading = document.createElement('h1');
  heading.className = 'admin-heading';
  heading.textContent = 'Prompt Library';
  header.appendChild(heading);
  
  const subheading = document.createElement('p');
  subheading.className = 'admin-subheading';
  subheading.textContent = 'Manage versioned prompts for AI content generation';
  header.appendChild(subheading);
  
  container.appendChild(header);
  
  // Create action bar
  const actionBar = document.createElement('div');
  actionBar.style.marginBottom = '1.5rem';
  
  const createBtn = document.createElement('button');
  createBtn.className = 'btn btn--primary';
  createBtn.textContent = '+ Create New Prompt';
  createBtn.addEventListener('click', () => {
    if (callbacks) callbacks.onCreatePrompt();
  });
  actionBar.appendChild(createBtn);
  
  container.appendChild(actionBar);
  
  // Create prompts list section
  const listSection = document.createElement('div');
  listSection.className = 'prompts-list';
  
  // Filters
  const filtersDiv = renderFilters();
  listSection.appendChild(filtersDiv);
  
  // Table container
  const tableContainer = document.createElement('div');
  tableContainer.id = 'prompts-table-container';
  listSection.appendChild(tableContainer);
  
  container.appendChild(listSection);
  
  // Load prompts
  await loadAndRenderPrompts(tableContainer);
}

/**
 * Render filters section
 */
function renderFilters(): HTMLElement {
  const filtersDiv = document.createElement('div');
  filtersDiv.className = 'clips-filters';
  
  // Status filter
  const statusFilter = createFilterSelect(
    'Status',
    'status',
    [
      { value: '', label: 'All Statuses' },
      { value: PromptStatus.Draft, label: 'Draft' },
      { value: PromptStatus.Approved, label: 'Approved' },
      { value: PromptStatus.Deprecated, label: 'Deprecated' }
    ],
    (value) => {
      currentFilters.status = value ? (value as PromptStatus) : undefined;
      currentPage = 1;
      const tableContainer = document.getElementById('prompts-table-container');
      if (tableContainer) loadAndRenderPrompts(tableContainer);
    }
  );
  filtersDiv.appendChild(statusFilter);
  
  // Language filter
  const languageFilter = createFilterSelect(
    'Language',
    'language',
    [
      { value: '', label: 'All Languages' },
      { value: 'tr', label: 'Turkish' },
      { value: 'de', label: 'German' },
      { value: 'en', label: 'English' }
    ],
    (value) => {
      currentFilters.language = value ? (value as 'tr' | 'de' | 'en') : undefined;
      currentPage = 1;
      const tableContainer = document.getElementById('prompts-table-container');
      if (tableContainer) loadAndRenderPrompts(tableContainer);
    }
  );
  filtersDiv.appendChild(languageFilter);
  
  // Level filter
  const levelFilter = createFilterSelect(
    'Level',
    'level',
    [
      { value: '', label: 'All Levels' },
      { value: 'B2', label: 'B2' },
      { value: 'B1', label: 'B1' }
    ],
    (value) => {
      currentFilters.level = value ? (value as 'B2' | 'B1') : undefined;
      currentPage = 1;
      const tableContainer = document.getElementById('prompts-table-container');
      if (tableContainer) loadAndRenderPrompts(tableContainer);
    }
  );
  filtersDiv.appendChild(levelFilter);
  
  return filtersDiv;
}

/**
 * Create a filter select element
 */
function createFilterSelect(
  label: string,
  name: string,
  options: Array<{ value: string; label: string }>,
  onChange: (value: string) => void
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'filter-group';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'filter-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `filter-${name}`;
  group.appendChild(labelEl);
  
  const select = document.createElement('select');
  select.id = `filter-${name}`;
  select.className = 'filter-select';
  
  options.forEach(opt => {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    select.appendChild(option);
  });
  
  select.addEventListener('change', () => onChange(select.value));
  
  group.appendChild(select);
  return group;
}

/**
 * Load and render prompts table
 */
async function loadAndRenderPrompts(container: HTMLElement): Promise<void> {
  try {
    // Show loading state
    container.innerHTML = '<div style="padding: 2rem; text-align: center;">Loading prompts...</div>';
    
    // Fetch prompts
    const response = await promptsService.listPrompts(currentFilters, currentPage, perPage);
    
    // Clear container
    container.innerHTML = '';
    
    if (response.prompts.length === 0) {
      container.innerHTML = '<div style="padding: 2rem; text-align: center; color: #6b7280;">No prompts found</div>';
      return;
    }
    
    // Create table
    const table = document.createElement('table');
    table.className = 'clips-table';
    
    // Table header
    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr>
        <th>Title</th>
        <th>Language</th>
        <th>Level</th>
        <th>Status</th>
        <th>Tags</th>
        <th>Created</th>
        <th>Actions</th>
      </tr>
    `;
    table.appendChild(thead);
    
    // Table body
    const tbody = document.createElement('tbody');
    response.prompts.forEach(prompt => {
      const row = createPromptRow(prompt);
      tbody.appendChild(row);
    });
    table.appendChild(tbody);
    
    container.appendChild(table);
    
    // Pagination
    if (response.hasMore || currentPage > 1) {
      const pagination = createPagination(response);
      container.appendChild(pagination);
    }
    
  } catch (error) {
    logger.error('admin: Failed to load prompts', { error });
    container.innerHTML = '<div style="padding: 2rem; text-align: center; color: #dc2626;">Failed to load prompts</div>';
  }
}

/**
 * Create a table row for a prompt
 */
function createPromptRow(prompt: PromptVersion): HTMLTableRowElement {
  const row = document.createElement('tr');
  
  // Title
  const titleCell = document.createElement('td');
  titleCell.className = 'clip-title-cell';
  titleCell.textContent = prompt.title;
  row.appendChild(titleCell);
  
  // Language
  const langCell = document.createElement('td');
  langCell.textContent = prompt.language.toUpperCase();
  row.appendChild(langCell);
  
  // Level
  const levelCell = document.createElement('td');
  levelCell.textContent = prompt.level;
  row.appendChild(levelCell);
  
  // Status
  const statusCell = document.createElement('td');
  const badge = createStatusBadge(prompt.status);
  statusCell.appendChild(badge);
  row.appendChild(statusCell);
  
  // Tags
  const tagsCell = document.createElement('td');
  if (prompt.tags && prompt.tags.length > 0) {
    const tagsSpan = document.createElement('span');
    tagsSpan.textContent = prompt.tags.join(', ');
    tagsSpan.style.fontSize = '0.875rem';
    tagsSpan.style.color = '#6b7280';
    tagsCell.appendChild(tagsSpan);
  } else {
    tagsCell.textContent = '—';
  }
  row.appendChild(tagsCell);
  
  // Created
  const createdCell = document.createElement('td');
  const date = new Date(prompt.created_at);
  createdCell.textContent = date.toLocaleDateString();
  row.appendChild(createdCell);
  
  // Actions
  const actionsCell = document.createElement('td');
  actionsCell.className = 'clip-actions';
  
  // View/Edit button
  const viewBtn = document.createElement('button');
  viewBtn.className = 'action-btn';
  viewBtn.textContent = 'View';
  viewBtn.addEventListener('click', () => {
    if (callbacks) callbacks.onEditPrompt(prompt.id);
  });
  actionsCell.appendChild(viewBtn);
  
  // Status-specific actions (Admin only)
  const userIsAdmin = isAdmin(); // Check if user has Admin role
  
  if (userIsAdmin && prompt.status === PromptStatus.Draft) {
    const approveBtn = document.createElement('button');
    approveBtn.className = 'action-btn action-btn--primary';
    approveBtn.textContent = 'Approve';
    approveBtn.addEventListener('click', async () => {
      await handleApprovePrompt(prompt.id);
    });
    actionsCell.appendChild(approveBtn);
    
    // Delete button for drafts
    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'action-btn action-btn--danger';
    deleteBtn.textContent = 'Delete';
    deleteBtn.addEventListener('click', async () => {
      await handleDeletePrompt(prompt.id);
    });
    actionsCell.appendChild(deleteBtn);
  }
  
  if (userIsAdmin && prompt.status === PromptStatus.Approved) {
    const deprecateBtn = document.createElement('button');
    deprecateBtn.className = 'action-btn action-btn--danger';
    deprecateBtn.textContent = 'Deprecate';
    deprecateBtn.addEventListener('click', async () => {
      await handleDeprecatePrompt(prompt.id);
    });
    actionsCell.appendChild(deprecateBtn);
  }
  
  row.appendChild(actionsCell);
  
  return row;
}

/**
 * Create a status badge
 */
function createStatusBadge(status: PromptStatus): HTMLElement {
  const badge = document.createElement('span');
  badge.className = 'state-badge';
  
  let colorClass = '';
  switch (status) {
    case PromptStatus.Draft:
      colorClass = 'state-badge--draft';
      break;
    case PromptStatus.Approved:
      colorClass = 'state-badge--approved';
      break;
    case PromptStatus.Deprecated:
      colorClass = 'state-badge--deprecated';
      break;
  }
  
  badge.classList.add(colorClass);
  badge.textContent = status.charAt(0).toUpperCase() + status.slice(1);
  
  return badge;
}

/**
 * Create pagination controls
 */
function createPagination(response: { hasMore: boolean; page: number }): HTMLElement {
  const pagination = document.createElement('div');
  pagination.className = 'pagination-container';
  
  if (currentPage > 1) {
    const prevBtn = document.createElement('button');
    prevBtn.className = 'action-btn';
    prevBtn.textContent = '← Previous';
    prevBtn.addEventListener('click', () => {
      currentPage--;
      const tableContainer = document.getElementById('prompts-table-container');
      if (tableContainer) loadAndRenderPrompts(tableContainer);
    });
    pagination.appendChild(prevBtn);
  }
  
  const pageInfo = document.createElement('span');
  pageInfo.className = 'pagination-info';
  pageInfo.textContent = `Page ${currentPage}`;
  pagination.appendChild(pageInfo);
  
  if (response.hasMore) {
    const nextBtn = document.createElement('button');
    nextBtn.className = 'action-btn';
    nextBtn.textContent = 'Next →';
    nextBtn.addEventListener('click', () => {
      currentPage++;
      const tableContainer = document.getElementById('prompts-table-container');
      if (tableContainer) loadAndRenderPrompts(tableContainer);
    });
    pagination.appendChild(nextBtn);
  }
  
  return pagination;
}

/**
 * Action handlers
 */
async function handleApprovePrompt(promptId: string): Promise<void> {
  try {
    await promptsService.approvePrompt(promptId);
    // Reload table
    const tableContainer = document.getElementById('prompts-table-container');
    if (tableContainer) await loadAndRenderPrompts(tableContainer);
    toast.success('Prompt approved');
  } catch (error) {
    logger.error('admin: Failed to approve prompt', { promptId, error });
    toast.error('Failed to approve prompt');
  }
}

async function handleDeprecatePrompt(promptId: string): Promise<void> {
  if (!confirm('Are you sure you want to deprecate this prompt?')) {
    return;
  }
  
  try {
    await promptsService.deprecatePrompt(promptId);
    const tableContainer = document.getElementById('prompts-table-container');
    if (tableContainer) await loadAndRenderPrompts(tableContainer);
    toast.success('Prompt deprecated');
  } catch (error) {
    logger.error('admin: Failed to deprecate prompt', { promptId, error });
    toast.error('Failed to deprecate prompt');
  }
}

async function handleDeletePrompt(promptId: string): Promise<void> {
  if (!confirm('Are you sure you want to delete this draft prompt? This action cannot be undone.')) {
    return;
  }
  
  try {
    await promptsService.deletePrompt(promptId);
    const tableContainer = document.getElementById('prompts-table-container');
    if (tableContainer) await loadAndRenderPrompts(tableContainer);
    toast.success('Prompt deleted');
  } catch (error) {
    logger.error('admin: Failed to delete prompt', { promptId, error });
    toast.error('Failed to delete prompt');
  }
}

/**
 * Render access denied message
 */
function renderAccessDenied(container: HTMLElement): void {
  container.innerHTML = `
    <div class="access-denied-container">
      <h2 class="access-denied-heading">Access Denied</h2>
      <p class="access-denied-message">You do not have permission to access the prompts library.</p>
    </div>
  `;
}
